
import * as React from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { DateRange } from "react-day-picker";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface DateRangePickerProps {
  className?: string;
  // Support both naming conventions for backward compatibility
  value?: DateRange;
  onChange?: (date: DateRange) => void;
  // Old naming convention
  date?: DateRange;
  onDateChange?: (date: DateRange) => void;
  setDate?: (date: DateRange) => void;
}

export function DateRangePicker({
  className,
  value,
  onChange,
  date,
  onDateChange,
  setDate,
}: DateRangePickerProps) {
  // Use either value/onChange or date/onDateChange based on what's provided
  const dateValue = value || date;
  
  const handleDateChange = (newDate: DateRange | undefined) => {
    if (!newDate) return;
    
    // Call all provided change handlers
    if (onChange) onChange(newDate);
    if (onDateChange) onDateChange(newDate);
    if (setDate) setDate(newDate);
  };

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            className={cn(
              "w-full justify-start text-left font-normal",
              !dateValue && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {dateValue?.from ? (
              dateValue.to ? (
                <>
                  {format(dateValue.from, "LLL dd, y")} - {format(dateValue.to, "LLL dd, y")}
                </>
              ) : (
                format(dateValue.from, "LLL dd, y")
              )
            ) : (
              <span>Pick a date range</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={dateValue?.from}
            selected={dateValue}
            onSelect={handleDateChange}
            numberOfMonths={2}
            className="pointer-events-auto"
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

// For backward compatibility with the existing DatePickerWithRange component
export { DateRangePicker as DatePickerWithRange };
